// src/pages/AdminAssets.jsx
import React, { useEffect, useMemo, useState, useRef } from "react";
import axios from "axios";
import TopNavbar from "../../Components/Navbar/Navbar";
import {
  Container, Row, Col, Card, Form, Button, Table, Spinner, Alert, Badge, Modal
} from "react-bootstrap";
import "./AdminAssets.css";
import { useLocation, useNavigate } from "react-router-dom";

const API_BASE = "https://api.assetview.warrantyindia.com/api";

const deltaBadge = (d) => {
  if (d == null) return <span className="delta-badge neutral">–</span>;
  if (d > 0) return <span className="delta-badge up">▲ +{d}</span>;
  if (d < 0) return <span className="delta-badge down">▼ {d}</span>;
  return <span className="delta-badge neutral">0</span>;
};

const conditionVariant = (c = "") => {
  const v = String(c).toLowerCase();
  if (v === "excellent") return "success";
  if (v === "good") return "primary";
  if (v === "fair") return "warning";
  if (v === "poor" || v === "damaged") return "danger";
  return "secondary";
};

const AdminAssets = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [filters, setFilters] = useState({
    search: "",
    username: "",
    Areaoffice: "",
    asset_type: "",
    year: ""
  });

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);

  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");

  // Edit Modal State
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState("");
  const [editSuccess, setEditSuccess] = useState("");

  // Edit Form State
  const [editForm, setEditForm] = useState({
    unique_no: "",
    username: "",
    Areaoffice: "",
    asset_type: "",
    serial_number: "",
    quantity: "",
    asset_value: "",
    condition: "",
    receiving_date: "",
    remarks: "",
    make: "",     
  model: ""     
  });

  // dropdown data
  const [userOptions, setUserOptions] = useState([]);
  const [typeOptions, setTypeOptions] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingTypes, setLoadingTypes] = useState(false);

  const [initialized, setInitialized] = useState(false);

  // ✅ prevent race-condition (ignore old responses)
  const reqIdRef = useRef(0);

  const totalPages = useMemo(() => Math.max(Math.ceil(total / limit), 1), [total, limit]);

  // ✅ 1) URL state param apply first
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const stateFromUrl = (params.get("state") || "").trim();

    if (stateFromUrl) {
      setFilters((f) => ({
        ...f,
        Areaoffice: stateFromUrl,
        username: "",
        asset_type: "",
        search: "",
        year: "",
      }));
      setPage(1);
    }

    setInitialized(true);
    // eslint-disable-next-line
  }, [location.search]);

  // ✅ 2) load usernames once
  useEffect(() => {
    const run = async () => {
      setLoadingUsers(true);
      try {
        const resp = await axios.get(`${API_BASE}/admin/assets/users`);
        if (resp.data?.ok) setUserOptions(resp.data.data || []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoadingUsers(false);
      }
    };
    run();
  }, []);

  // ✅ 3) load asset types whenever username changes
  useEffect(() => {
    const run = async () => {
      setLoadingTypes(true);
      try {
        const resp = await axios.get(`${API_BASE}/admin/assets/asset-types`, {
          params: { username: filters.username || "" },
        });
        if (resp.data?.ok) setTypeOptions(resp.data.data || []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoadingTypes(false);
      }
    };
    run();
  }, [filters.username]);

  // ✅ 4) fetchData (single source of truth)
  const fetchData = async (activeFilters, activePage) => {
    const myReqId = ++reqIdRef.current;

    setPending(true);
    setError("");

    try {
      const resp = await axios.get(`${API_BASE}/admin/assets`, {
        params: { page: activePage, limit, ...activeFilters },
      });

      // ✅ ignore stale responses
      if (myReqId !== reqIdRef.current) return;

      if (resp.data?.ok) {
        setRows(resp.data.data || []);
        setTotal(resp.data.total || 0);
      } else {
        setError(resp.data?.error || "Failed to load data");
      }
    } catch (err) {
      if (myReqId !== reqIdRef.current) return;
      console.error(err);
      setError("Failed to load data");
    } finally {
      if (myReqId === reqIdRef.current) setPending(false);
    }
  };

  // ✅ 5) single effect to fetch (NO DUPLICATE)
  useEffect(() => {
    if (!initialized) return;
    fetchData(filters, page);
    // eslint-disable-next-line
  }, [initialized, page, filters]);

  // handlers
  const handleText = (e) => {
    const { name, value } = e.target;
    setFilters((f) => ({ ...f, [name]: value }));
    setPage(1);
  };

  const handleUsername = (e) => {
    const value = e.target.value;
    setFilters((f) => ({
      ...f,
      username: value,
      asset_type: "",
    }));
    setPage(1);
  };

  const handleType = (e) => {
    const value = e.target.value;
    setFilters((f) => ({ ...f, asset_type: value }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({
      search: "",
      username: "",
      Areaoffice: "",
      asset_type: "",
      year: "",
    });
    setPage(1);
  };

  // ✅ Edit Handlers
  const handleEditClick = async (asset) => {
    setEditLoading(true);
    setEditError("");
    setEditSuccess("");
    
    try {
      // Fetch full asset details
      const response = await axios.get(`${API_BASE}/admin/assets/${asset.id}`);
      
      if (response.data?.ok) {
        const data = response.data.data;
        setEditingAsset(data);
        setEditForm({
          unique_no: data.unique_no || "",
          username: data.username || "",
          Areaoffice: data.Areaoffice || "",
          asset_type: data.asset_type || "",
          serial_number: data.serial_number || "",
          quantity: data.quantity || "",
          asset_value: data.asset_value || "",
          condition: data.condition || "",
          receiving_date: data.receiving_date ? data.receiving_date.split('T')[0] : "",
          remarks: data.remarks || "",
          make: data.make || "",      
        model: data.model || ""      
        });
        setShowEditModal(true);
      } else {
        setEditError("Failed to load asset details");
      }
    } catch (err) {
      console.error("Error fetching asset:", err);
      setEditError("Failed to load asset details");
    } finally {
      setEditLoading(false);
    }
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEditSubmit = async () => {
  // Validation
  if (!editForm.unique_no.trim()) {
    setEditError("Unique number is required");
    return;
  }
  if (!editForm.username.trim()) {
    setEditError("Username is required");
    return;
  }
  if (!editForm.Areaoffice.trim()) {
    setEditError("Area is required");
    return;
  }
  if (!editForm.asset_type.trim()) {
    setEditError("Asset type is required");
    return;
  }
  if (!editForm.quantity || isNaN(editForm.quantity) || editForm.quantity < 0) {
    setEditError("Please enter a valid quantity");
    return;
  }

  setEditLoading(true);
  setEditError("");
  setEditSuccess("");

  try {
    const response = await axios.put(
      `${API_BASE}/admin/assets/${editingAsset.id}`,
      {
        unique_no: editForm.unique_no,
        username: editForm.username,
        Areaoffice: editForm.Areaoffice,
        asset_type: editForm.asset_type,
        serial_number: editForm.serial_number,
        quantity: editForm.quantity,
        asset_value: editForm.asset_value,
        condition: editForm.condition,
        receiving_date: editForm.receiving_date,
        remarks: editForm.remarks,
        make: editForm.make,      // ✅ Added
        model: editForm.model      // ✅ Added
      }
    );

    if (response.data.ok) {
      setEditSuccess("✅ Asset updated successfully!");
      setTimeout(() => {
        setShowEditModal(false);
        fetchData(filters, page);
      }, 1500);
    } else {
      setEditError(response.data.error || "Failed to update asset");
    }
  } catch (err) {
    console.error("Edit error:", err);
    setEditError(err.response?.data?.error || "Failed to update asset");
  } finally {
    setEditLoading(false);
  }
};

  return (
    <>
      <TopNavbar />
      <div className="adminassets-page">
        <Container fluid className="py-4" style={{ marginTop: "-50px", width: "76vw" }}>
          <Button
            variant="outline-secondary"
            className="ms-3"
            onClick={() => navigate("/admin-assets-summary")}
          >
            <i className="fa-solid fa-arrow-left me-2"></i>
            Back
          </Button>
          <Row className="justify-content-center">
            <Col xl={11} lg={11} style={{ width: "100%" }}>
              <Card className="assets-card shadow-lg border-0">
                <Card.Header className="assets-card-header py-4">
                  <div className="d-flex align-items-center justify-content-between gap-3">
                    <div className="d-flex align-items-center gap-3">
                      <div className="header-icon">
                        <i className="fa-solid fa-chart-line"></i>
                      </div>
                      <div className="d-flex">
                        <h1 className="page-title mb-1">All Assets</h1>

                        {filters.Areaoffice ? (
                          <div className="text-muted" style={{ fontSize: 13 }}>
                            Showing State: <b>{filters.Areaoffice}</b>
                          </div>
                        ) : null}
                      </div>
                    </div>

                    <Form className="filters-wrap">
                      <div className="filters-grid">
                        <Form.Control
                          placeholder="Search everything"
                          name="search"
                          value={filters.search}
                          onChange={handleText}
                          className="form-control-professional"
                        />
                      </div>
                    </Form>
                  </div>
                </Card.Header>

                <Card.Body className="p-0">
                  {error && (
                    <div className="p-3">
                      <Alert variant="danger" className="alert-fade-in mb-3">{error}</Alert>
                    </div>
                  )}

                  {pending ? (
                    <div className="loader-wrap">
                      <Spinner animation="border" />
                      <div className="loader-label">Loading all assets…</div>
                    </div>
                  ) : (
                    <>
                      <div className="table-responsive adminassets-table-wrap">
                        <Table hover className="adminassets-table mb-0">
                          <thead>
                            <tr>
                              <th>#</th>
                              <th>Unique No</th>
                              <th>User</th>
                              <th>END USER</th>
                              <th>Area</th>
                              <th>Asset Type</th>
                              <th>Make</th>        
    <th>Model</th>         
                              <th>Serial Number</th>
                              <th>Qty</th>
                              <th>Δ Change</th>
                              <th>Receiving</th>
                              <th>Assets Value</th>
                              <th>Condition</th>
                              <th className="remarks-cell">Remarks</th>
                              <th>Created</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {rows.length === 0 ? (
                              <tr>
                                <td colSpan={16} className="empty-cell">
                                  <div className="empty-state">
                                    <i className="fa-regular fa-folder-open"></i>
                                    <p>No records found</p>
                                  </div>
                                </td>
                              </tr>
                            ) : (
                              rows.map((r, idx) => (
                                <tr key={r.id}>
                                  <td>{(page - 1) * limit + idx + 1}</td>
                                  <td>{r.unique_no}</td>
                                  <td>{r.username}</td>
                                  <td>{r.device_user_name}</td>
                                  <td>{r.Areaoffice}</td>
                                  <td className="text-capitalize">{r.asset_type}</td>
                                  <td>{r.make || "-"}</td>        
    <td>{r.model || "-"}</td>        
                                  <td>{r.serial_number || "-"}</td>
                                  <td><Badge bg="dark">{r.quantity}</Badge></td>
                                  <td>{deltaBadge(r.delta)}</td>
                                  <td>
                                    {r.receiving_date && !isNaN(new Date(r.receiving_date))
                                      ? new Date(r.receiving_date).getFullYear()
                                      : r.receiving_date || "-"}
                                  </td>
                                  <td>{r.asset_value}</td>
                                  <td>
                                    <Badge bg={conditionVariant(r.condition)} pill>
                                      {r.condition || "-"}
                                    </Badge>
                                  </td>
                                  <td className="remarks-cell">{r.remarks}</td>
                                  <td>{new Date(r.created_at).toLocaleString()}</td>
                                  <td>
                                    <Button
                                      variant="primary"
                                      size="sm"
                                      onClick={() => handleEditClick(r)}
                                      className="edit-btn"
                                    >
                                      <i className="fa-solid fa-pen"></i> Edit
                                    </Button>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </Table>
                      </div>

                      <div className="table-footer">
                        <div className="text-muted">
                          Showing {(rows.length && (page - 1) * limit + 1) || 0}–
                          {(page - 1) * limit + rows.length} of {total}
                        </div>
                        <div className="pager">
                          <Button
                            variant="outline-secondary"
                            className="btn-nav"
                            disabled={page <= 1}
                            onClick={() => setPage((p) => Math.max(p - 1, 1))}
                          >
                            <i className="fa-solid fa-angle-left me-2"></i>Prev
                          </Button>
                          <span className="page-indicator">Page {page} / {totalPages}</span>
                          <Button
                            variant="outline-secondary"
                            className="btn-nav"
                            disabled={page >= totalPages}
                            onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
                          >
                            Next<i className="fa-solid fa-angle-right ms-2"></i>
                          </Button>
                        </div>
                      </div>
                    </>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* ✅ Edit Modal */}
      <Modal
        show={showEditModal}
        onHide={() => {
          setShowEditModal(false);
          setEditError("");
          setEditSuccess("");
        }}
        centered
        size="lg"
        backdrop="static"
      >
        <Modal.Header closeButton className="border-0">
          <Modal.Title>
            <i className="fa-solid fa-pen-to-square me-2 text-primary"></i>
            Edit Asset Entry
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editSuccess && (
            <Alert variant="success" className="alert-fade-in">
              {editSuccess}
            </Alert>
          )}
          {editError && (
            <Alert variant="danger" className="alert-fade-in">
              {editError}
            </Alert>
          )}

          {editingAsset && (
            <Form>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold">Unique No *</Form.Label>
                    <Form.Control
                      type="text"
                      name="unique_no"
                      value={editForm.unique_no}
                      onChange={handleEditFormChange}
                      disabled={editLoading}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold">Username *</Form.Label>
                    <Form.Control
                      type="text"
                      name="username"
                      value={editForm.username}
                      onChange={handleEditFormChange}
                      disabled={editLoading}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold">Area *</Form.Label>
                    <Form.Control
                      type="text"
                      name="Areaoffice"
                      value={editForm.Areaoffice}
                      onChange={handleEditFormChange}
                      disabled={editLoading}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold">Asset Type *</Form.Label>
                    <Form.Control
                      type="text"
                      name="asset_type"
                      value={editForm.asset_type}
                      onChange={handleEditFormChange}
                      disabled={editLoading}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
    <Form.Group className="mb-3">
      <Form.Label className="fw-bold">Make</Form.Label>
      <Form.Control
        type="text"
        name="make"
        value={editForm.make}
        onChange={handleEditFormChange}
        disabled={editLoading}
        placeholder="Enter make"
      />
    </Form.Group>
  </Col>
              </Row>

              <Row>
                <Col md={6}>
    <Form.Group className="mb-3">
      <Form.Label className="fw-bold">Model</Form.Label>
      <Form.Control
        type="text"
        name="model"
        value={editForm.model}
        onChange={handleEditFormChange}
        disabled={editLoading}
        placeholder="Enter model"
      />
    </Form.Group>
  </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold">Serial Number</Form.Label>
                    <Form.Control
                      type="text"
                      name="serial_number"
                      value={editForm.serial_number}
                      onChange={handleEditFormChange}
                      disabled={editLoading}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold">Quantity *</Form.Label>
                    <Form.Control
                      type="number"
                      name="quantity"
                      min="0"
                      value={editForm.quantity}
                      onChange={handleEditFormChange}
                      disabled={editLoading}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold">Asset Value</Form.Label>
                    <Form.Control
                      type="text"
                      name="asset_value"
                      value={editForm.asset_value}
                      onChange={handleEditFormChange}
                      disabled={editLoading}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold">Condition</Form.Label>
                    <Form.Select
                      name="condition"
                      value={editForm.condition}
                      onChange={handleEditFormChange}
                      disabled={editLoading}
                    >
                      <option value="">Select Condition</option>
                      <option value="Excellent">Excellent</option>
                      <option value="Good">Good</option>
                      <option value="Fair">Fair</option>
                      <option value="Poor">Poor</option>
                      <option value="Damaged">Damaged</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold">Receiving Date</Form.Label>
                    <Form.Control
                      type="date"
                      name="receiving_date"
                      value={editForm.receiving_date}
                      onChange={handleEditFormChange}
                      disabled={editLoading}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold">Remarks</Form.Label>
                    <Form.Control
                      type="text"
                      name="remarks"
                      value={editForm.remarks}
                      onChange={handleEditFormChange}
                      disabled={editLoading}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button
            variant="secondary"
            onClick={() => {
              setShowEditModal(false);
              setEditError("");
              setEditSuccess("");
            }}
            disabled={editLoading}
          >
            <i className="fa-solid fa-times me-2"></i>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleEditSubmit}
            disabled={editLoading}
            className="px-4"
          >
            {editLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                Saving...
              </>
            ) : (
              <>
                <i className="fa-solid fa-save me-2"></i>
                Save Changes
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AdminAssets;